import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, TextField, Button, Stepper, Step, StepLabel,
  CircularProgress, Alert, Card, CardContent, Grid, Divider, Chip, Breadcrumbs, Link
} from '@mui/material';
import {
  Search as SearchIcon, Home as HomeIcon, LocalShipping as ShippingIcon,
  CheckCircle, Cancel, Schedule, Info
} from '@mui/icons-material';
import { trackBostaByTrackingNumber } from '../../services/bostaService';
import { BostaTrackingResponse, BOSTA_STATE_MAP } from '../../types/bosta';

const stateColors: Record<string, string> = {
  '45': '#4caf50', '46': '#4caf50',  // delivered/returned - success
  '10': '#2196f3', '20': '#2196f3', '21': '#2196f3', '22': '#2196f3', '24': '#2196f3',
  '30': '#2196f3', '40': '#2196f3', '41': '#2196f3', '11': '#2196f3',  // in progress - info
  '47': '#ff9800', '48': '#ff9800', '49': '#f44336',  // exception/cancelled - warning/danger
  '100': '#f44336', '101': '#f44336'  // lost/damaged - danger
};

const TrackingPage: React.FC = () => {
  const { trackingNumber: paramTrackingNumber } = useParams<{ trackingNumber?: string }>();
  const [searchQuery, setSearchQuery] = useState(paramTrackingNumber || '');
  const [trackingData, setTrackingData] = useState<BostaTrackingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (paramTrackingNumber) {
      handleTrack(paramTrackingNumber);
    }
  }, [paramTrackingNumber]);

  const handleTrack = async (query?: string) => {
    const q = query || searchQuery;
    if (!q.trim()) {
      setError('يرجى إدخال رقم التتبع');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await trackBostaByTrackingNumber(q.trim());
      if (data) {
        setTrackingData(data);
      } else {
        setError('لم يتم العثور على شحنة بهذا الرقم');
        setTrackingData(null);
      }
    } catch (err) {
      setError('حدث خطأ أثناء البحث. تأكد من صحة رقم التتبع');
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (state: number) => {
    if (state === 45 || state === 46) return <CheckCircle sx={{ color: '#4caf50' }} />;
    if (state === 49) return <Cancel sx={{ color: '#f44336' }} />;
    if (state === 47 || state === 48) return <Info sx={{ color: '#ff9800' }} />;
    return <Schedule sx={{ color: '#2196f3' }} />;
  };

  const formatDate = (timestamp: string) => {
    const d = new Date(timestamp);
    return d.toLocaleString('ar-EG');
  };

  const getProgressValue = (state: number): number => {
    if (state === 10 || state === 11) return 10;
    if (state === 20 || state === 21 || state === 24) return 30;
    if (state === 22 || state === 40 || state === 41) return 50;
    if (state === 30) return 70;
    if (state === 45 || state === 46) return 100;
    if (state === 47 || state === 48 || state === 49) return 0;
    return 0;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link component={RouterLink} to="/" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> الرئيسية
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <ShippingIcon sx={{ mr: 0.5 }} fontSize="inherit" /> تتبع الشحنة
          </Typography>
        </Breadcrumbs>
      </Box>

      <Paper sx={{ p: 4, mb: 4, textAlign: 'center' }}>
        <ShippingIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>تتبع الشحنة</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          أدخل رقم التتبع الخاص بشحنتك لمعرفة حالتها الحالية
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, maxWidth: 500, mx: 'auto' }}>
          <TextField fullWidth label="رقم التتبع" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
            dir="ltr" />
          <Button variant="contained" onClick={() => handleTrack()} disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}>
            بحث
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {trackingData && (
        <>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">رقم التتبع</Typography>
                  <Typography variant="h6" dir="ltr">{trackingData.trackingNumber}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">الحالة الحالية</Typography>
                  <Chip label={trackingData.stateName || BOSTA_STATE_MAP[trackingData.state] || `الحالة ${trackingData.state}`}
                    sx={{
                      bgcolor: stateColors[trackingData.state] || '#9e9e9e',
                      color: '#fff', fontWeight: 'bold', mt: 0.5
                    }} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">نوع الشحنة</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {trackingData.type === 'SEND' ? 'توصيل' :
                     trackingData.type === 'EXCHANGE' ? 'استبدال' :
                     trackingData.type === 'CUSTOMER_RETURN_PICKUP' ? 'استرجاع' : trackingData.type}
                  </Typography>
                </Grid>
                {trackingData.cod > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">المبلغ المستحق</Typography>
                    <Typography variant="h6" color="primary">{trackingData.cod.toLocaleString('ar-EG')} ج.م</Typography>
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>تقدم الشحنة</Typography>
                <Box sx={{
                  height: 8, bgcolor: '#e0e0e0', borderRadius: 4, overflow: 'hidden', position: 'relative'
                }}>
                  <Box sx={{
                    height: '100%', bgcolor: getProgressValue(trackingData.state) === 0 ? '#f44336' : '#4caf50',
                    borderRadius: 4, width: `${Math.max(getProgressValue(trackingData.state), 5)}%`,
                    transition: 'width 0.5s ease'
                  }} />
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Typography variant="h6" gutterBottom>سجل التتبع</Typography>
          <Paper sx={{ p: 2 }}>
            {trackingData.events && trackingData.events.length > 0 ? (
              <Stepper orientation="vertical" sx={{ '& .MuiStepLabel-iconContainer': { display: 'none' } }}>
                {[...trackingData.events].reverse().map((event, index) => (
                  <Step key={index} active>
                    <StepLabel>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {getEventIcon(event.state)}
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {event.stateName || BOSTA_STATE_MAP[event.state] || `الحالة ${event.state}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(event.timestamp)}
                          </Typography>
                          {event.exceptionReason && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                              {event.exceptionReason}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            ) : (
              <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                {trackingData.stateName || BOSTA_STATE_MAP[trackingData.state] || `الحالة ${trackingData.state}`}
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default TrackingPage;
